
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { ttsApi, type TtsRunsParams } from '../api/endpoints';
import type {
  Id,
  Page,
  TtsRun,
  TtsSegment,
  TtsSynthesizeRequest,
  TtsToolResponse,
  VoiceCatalogItem,
} from '../types/api';
import { isTerminal } from '../types/api';
import { POLL_INTERVAL_MS, queryKeys } from './keys';
import { renewDelayMs } from './useMedia';

export function useProviderVoices(
  options: { enabled?: boolean } = {},
): UseQueryResult<VoiceCatalogItem[], ApiError> {
  return useQuery<VoiceCatalogItem[], ApiError>({
    queryKey: queryKeys.ttsProviderVoices,
    queryFn: () => ttsApi.providerVoices(),
    enabled: options.enabled ?? true,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => error.isRetryable && error.status !== 503 && failureCount < 2,
  });
}

export function useTtsRuns(params: TtsRunsParams = {}): UseQueryResult<Page<TtsRun>, ApiError> {
  return useQuery<Page<TtsRun>, ApiError>({
    queryKey: queryKeys.ttsRuns(params),
    queryFn: () => ttsApi.runs(params),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const items = query.state.data?.items;
      if (!items) return false;
      return items.some((run) => !isTerminal(run.status)) ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useTtsRun(runId: Id | undefined): UseQueryResult<TtsToolResponse, ApiError> {
  return useQuery<TtsToolResponse, ApiError>({
    queryKey: queryKeys.ttsRun(runId ?? ''),
    queryFn: () => ttsApi.run(runId as Id),
    enabled: Boolean(runId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => renewDelayMs(earliestExpiry(query.state.data)),
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

function earliestExpiry(run: TtsToolResponse | undefined): string | undefined {
  const stamps = (run?.segments ?? []).map((s) => s.audio.expires_at).filter(Boolean);
  if (stamps.length === 0) return undefined;
  return stamps.reduce((earliest, item) => (Date.parse(item) < Date.parse(earliest) ? item : earliest));
}

export function useSynthesize() {
  const qc = useQueryClient();
  return useMutation<TtsToolResponse, ApiError, TtsSynthesizeRequest>({
    mutationFn: (payload) => ttsApi.synthesize(payload),
    retry: false,
    onSuccess: (run) => {
      qc.setQueryData(queryKeys.ttsRun(run.run_id), run);
      void qc.invalidateQueries({ queryKey: queryKeys.ttsRoot });
    },
  });
}

export function runDurationSeconds(run: TtsToolResponse | undefined): number {
  return (run?.total_duration_ms ?? 0) / 1000;
}

export function degradedSegments(run: TtsToolResponse | undefined): TtsSegment[] {
  return (run?.segments ?? []).filter((segment) => segment.degraded);
}

export function findCatalogVoice(
  catalog: readonly VoiceCatalogItem[] | undefined,
  voiceId: string | undefined,
): VoiceCatalogItem | null {
  if (!voiceId) return null;
  return (catalog ?? []).find((item) => item.voice_id === voiceId) ?? null;
}
